import './style.css';
import { Marked } from 'marked';
import markedFootnote from 'marked-footnote';
import * as THREE from 'three';
import aboutMarkdown from './about.md?raw';
import { ArchitecturalModeler } from './modeler.js';
import { HandInputController } from './hand-tracker.js';

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];
const AUTOSAVE_KEY = 'massa.project.v3';

const elements = {
  canvas: $('#scene-canvas'),
  viewport: $('#viewport-shell'),
  handCursor: $('#hand-cursor'),
  video: $('#hand-video'),
  handOverlay: $('#hand-overlay'),
  cameraPlaceholder: $('#camera-placeholder'),
  cameraState: $('#camera-state'),
  cameraButton: $('#toggle-camera'),
  topStatus: $('#top-status'),
  interactionStatus: $('#interaction-status'),
  modeTitle: $('#mode-title'),
  modeHelp: $('#mode-help'),
  selectionMetric: $('#selection-metric'),
  selectionChip: $('#selection-chip'),
  emptyInspector: $('#empty-inspector'),
  inspectorForm: $('#inspector-form'),
  undo: $('#undo'),
  redo: $('#redo'),
  toastRegion: $('#toast-region'),
  articleContent: $('#article-content'),
  massName: $('#mass-name'),
  massWidth: $('#mass-width'),
  massDepth: $('#mass-depth'),
  massHeight: $('#mass-height'),
  massRotation: $('#mass-rotation'),
  massX: $('#mass-x'),
  massZ: $('#mass-z'),
  openingWidth: $('#opening-width'),
  openingHeight: $('#opening-height'),
  openingSill: $('#opening-sill'),
  saveState: $('#save-state'),
  projectFile: $('#project-file'),
};

let persistenceReady = false;
let saveTimer = null;

function showToast(message, type = 'info', duration = 3600) {
  const toast = document.createElement('div');
  toast.className = `toast${type === 'error' ? ' is-error' : ''}`;
  toast.textContent = message;
  elements.toastRegion.append(toast);
  window.setTimeout(() => toast.remove(), duration);
}

function setInteractionStatus(message) {
  elements.interactionStatus.textContent = message;
}

function updateSelection(data) {
  const hasSelection = Boolean(data);
  elements.emptyInspector.hidden = hasSelection;
  elements.inspectorForm.hidden = !hasSelection;

  if (!data) {
    elements.selectionChip.textContent = '—';
    elements.selectionMetric.textContent = 'Нет выбора';
    return;
  }

  const count = data.holes.length;
  const lastTwo = count % 100;
  const last = count % 10;
  const openingWord = lastTwo >= 11 && lastTwo <= 14 ? 'проёмов' : last === 1 ? 'проём' : last >= 2 && last <= 4 ? 'проёма' : 'проёмов';
  elements.selectionChip.textContent = `${count} ${openingWord}`;
  elements.selectionMetric.textContent = `${data.width.toFixed(1)} × ${data.depth.toFixed(1)} × ${data.height.toFixed(1)} м`;
  elements.massName.value = data.name;
  elements.massWidth.value = data.width.toFixed(1);
  elements.massDepth.value = data.depth.toFixed(1);
  elements.massHeight.value = data.height.toFixed(1);
  elements.massRotation.value = Math.round(THREE.MathUtils.radToDeg(data.rotationY));
  elements.massX.value = data.position.x.toFixed(1);
  elements.massZ.value = data.position.z.toFixed(1);
}

function updateToolUI({ tool, title, help }) {
  $$('[data-tool]').forEach((button) => button.classList.toggle('is-active', button.dataset.tool === tool));
  elements.modeTitle.textContent = title;
  elements.modeHelp.textContent = help;
}

function setSaveState(message) {
  elements.saveState.textContent = message;
}

function saveProjectLocally(project) {
  if (!persistenceReady) return;
  try {
    const payload = {
      savedAt: new Date().toISOString(),
      project,
    };
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload));
    setSaveState(`сохранено ${new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`);
  } catch (error) {
    console.warn('Massa autosave failed', error);
    setSaveState('не сохранено');
  }
}

function scheduleAutosave(project) {
  if (!persistenceReady) return;
  setSaveState('сохранение…');
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => saveProjectLocally(project), 260);
}

function readAutosave() {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const project = parsed?.project ?? parsed;
    if (!['massa-project', 'airform-project'].includes(project?.format) || !Array.isArray(project.masses)) return null;
    return project;
  } catch (error) {
    console.warn('Massa autosave could not be read', error);
    return null;
  }
}

const modeler = new ArchitecturalModeler(elements.canvas, {
  onSelectionChange: updateSelection,
  onHistoryChange: ({ canUndo, canRedo }) => {
    elements.undo.disabled = !canUndo;
    elements.redo.disabled = !canRedo;
  },
  onStatus: setInteractionStatus,
  onToolChange: updateToolUI,
  onModelChange: scheduleAutosave,
});

const handInput = new HandInputController({
  video: elements.video,
  overlay: elements.handOverlay,
  cursor: elements.handCursor,
  viewport: elements.viewport,
  onPointerDown: (x, y) => modeler.inputDown(x, y, 'hand'),
  onPointerMove: (x, y) => modeler.inputMove(x, y, 'hand'),
  onPointerUp: (x, y) => modeler.inputUp(x, y, 'hand'),
  onStatus: setInteractionStatus,
  onState: ({ state, label }) => {
    elements.cameraState.textContent = label;
    const active = state === 'running';
    const loading = state === 'loading';
    elements.cameraPlaceholder.hidden = active;
    elements.cameraButton.disabled = loading;
    elements.cameraButton.textContent = loading
      ? 'Загрузка модели руки…'
      : active
        ? 'Выключить управление рукой'
        : 'Включить управление рукой';

    const statusLabel = active ? `Рука: ${label}` : state === 'error' ? 'Ошибка камеры' : 'Мышь активна';
    elements.topStatus.lastElementChild.textContent = statusLabel;
    elements.topStatus.querySelector('.status-dot').style.background = state === 'error' ? 'var(--danger)' : 'var(--accent)';
  },
});

function readInspectorValues() {
  return {
    name: elements.massName.value,
    width: Number(elements.massWidth.value),
    depth: Number(elements.massDepth.value),
    height: Number(elements.massHeight.value),
    rotationY: THREE.MathUtils.degToRad(Number(elements.massRotation.value)),
    position: {
      x: Number(elements.massX.value),
      z: Number(elements.massZ.value),
    },
  };
}

function updateOpening() {
  const corrected = modeler.setOpeningSize({
    width: Number(elements.openingWidth.value),
    height: Number(elements.openingHeight.value),
    sill: Number(elements.openingSill.value),
  });
  elements.openingWidth.value = corrected.width.toFixed(1);
  elements.openingHeight.value = corrected.height.toFixed(1);
  elements.openingSill.value = corrected.sill.toFixed(1);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function dateStamp() {
  return new Date().toISOString().replaceAll(':', '-').slice(0, 19);
}

function exportJSON() {
  const project = {
    ...modeler.serializeProject(),
    exportedAt: new Date().toISOString(),
    generator: 'Massa · DØMO LAB MVP 1.3',
  };
  const text = JSON.stringify(project, null, 2);
  downloadBlob(new Blob([text], { type: 'application/json' }), `massa-${dateStamp()}.json`);
  showToast('Параметрический проект JSON сохранён.');
}

function exportMeshText(extension) {
  const objText = modeler.exportOBJ();
  const isObg = extension === 'obg';
  const header = isObg
    ? `# Massa OBG compatibility export\n# Geometry below follows the Wavefront OBJ text syntax.\n`
    : '';
  const text = `${header}${objText}`;
  downloadBlob(new Blob([text], { type: 'text/plain;charset=utf-8' }), `massa-${dateStamp()}.${extension}`);
  showToast(isObg ? 'OBG сохранён как совместимый текстовый экспорт геометрии OBJ.' : 'OBJ-модель сохранена.');
}

function setView(viewName, updateHash = true) {
  const target = viewName === 'article' ? 'article' : 'studio';
  $$('.view').forEach((view) => view.classList.toggle('is-active', view.id === `${target}-view`));
  $$('.view-tab').forEach((tab) => tab.classList.toggle('is-active', tab.dataset.view === target));
  if (updateHash) history.replaceState(null, '', `#${target}`);
  if (target === 'studio') requestAnimationFrame(() => modeler.resize());
  else handInput.stop();
}

$$('[data-tool]').forEach((button) => {
  button.addEventListener('click', () => modeler.setTool(button.dataset.tool));
});

$('#add-mass').addEventListener('click', () => {
  const count = modeler.serializeProject().masses.length;
  const index = Math.max(0, count - 2);
  modeler.addMass({
    position: {
      x: (index % 4) * 1.2 - 1.8,
      z: -5.2 - Math.floor(index / 4) * 1.2,
    },
  });
});

$('#duplicate-selected').addEventListener('click', () => modeler.duplicateSelected());
$('#rotate-selected').addEventListener('click', () => modeler.rotateSelected(15));
$('#delete-selected').addEventListener('click', () => modeler.deleteSelected());
$('#reset-view').addEventListener('click', () => modeler.resetView());
elements.undo.addEventListener('click', () => modeler.undo());
elements.redo.addEventListener('click', () => modeler.redo());

elements.inspectorForm.addEventListener('change', () => modeler.updateSelected(readInspectorValues()));
[elements.openingWidth, elements.openingHeight, elements.openingSill].forEach((input) => {
  input.addEventListener('change', updateOpening);
});

elements.cameraButton.addEventListener('click', async () => {
  try {
    await handInput.toggle();
  } catch (error) {
    const reason = error?.message || String(error);
    showToast(`Камера не запущена: ${reason}`, 'error', 6500);
    setInteractionStatus(`Камера не запущена: ${reason}`);
  }
});

$('#new-project').addEventListener('click', () => {
  const proceed = window.confirm('Создать новый проект? Текущую версию можно предварительно сохранить в JSON.');
  if (!proceed) return;
  modeler.newProject();
  modeler.resetView();
  showToast('Создан новый проект со стартовой массой и входным проёмом.');
});

$('#import-json').addEventListener('click', () => elements.projectFile.click());
elements.projectFile.addEventListener('change', async () => {
  const file = elements.projectFile.files?.[0];
  elements.projectFile.value = '';
  if (!file) return;

  try {
    const parsed = JSON.parse(await file.text());
    const project = parsed?.project ?? parsed;
    modeler.loadProject(project, { resetHistory: true });
    modeler.resetView();
    showToast(`Проект «${file.name}» открыт.`);
  } catch (error) {
    showToast(`Не удалось открыть JSON: ${error.message || error}`, 'error', 6500);
  }
});

$('#export-glb').addEventListener('click', async () => {
  const button = $('#export-glb');
  const original = button.textContent;
  button.disabled = true;
  button.textContent = 'Формирование GLB…';
  try {
    const buffer = await modeler.exportGLB();
    downloadBlob(new Blob([buffer], { type: 'model/gltf-binary' }), `massa-${dateStamp()}.glb`);
    showToast('GLB сформирован. Одна единица модели равна одному метру.');
  } catch (error) {
    showToast(`Не удалось экспортировать GLB: ${error.message || error}`, 'error', 6500);
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
});

$('#export-obj').addEventListener('click', () => exportMeshText('obj'));
$('#export-obg').addEventListener('click', () => exportMeshText('obg'));

$('#export-json').addEventListener('click', exportJSON);

$('#export-png').addEventListener('click', async () => {
  const button = $('#export-png');
  const original = button.textContent;
  button.disabled = true;
  button.textContent = 'Снимок…';
  try {
    const blob = await modeler.capturePNG();
    downloadBlob(blob, `massa-${dateStamp()}.png`);
    showToast('PNG-снимок рабочей сцены сохранён.');
  } catch (error) {
    showToast(`Не удалось сохранить PNG: ${error.message || error}`, 'error', 6500);
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
});

$$('.view-tab').forEach((button) => button.addEventListener('click', () => setView(button.dataset.view)));
$('.brand').addEventListener('click', (event) => {
  event.preventDefault();
  setView('studio');
});

window.addEventListener('hashchange', () => setView(location.hash.slice(1), false));

window.addEventListener('keydown', (event) => {
  const editing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
  if (editing) return;

  const modifier = event.ctrlKey || event.metaKey;
  if (modifier && event.key.toLowerCase() === 'z') {
    event.preventDefault();
    if (event.shiftKey) modeler.redo();
    else modeler.undo();
    return;
  }
  if (modifier && event.key.toLowerCase() === 'y') {
    event.preventDefault();
    modeler.redo();
    return;
  }
  if (modifier && event.key.toLowerCase() === 's') {
    event.preventDefault();
    exportJSON();
    return;
  }

  if (event.key === '1') modeler.setTool('move');
  if (event.key === '2') modeler.setTool('height');
  if (event.key === '3') modeler.setTool('cut');
  if (event.key === '4') modeler.duplicateSelected();
  if (event.key === 'Delete' || event.key === 'Backspace') modeler.deleteSelected();
  if (event.key === 'Escape') modeler.cancelExternalInteraction();
});

const markdown = new Marked({ gfm: true, breaks: false }).use(markedFootnote());
elements.articleContent.innerHTML = markdown.parse(aboutMarkdown);
elements.articleContent.querySelectorAll('a').forEach((link) => {
  if (link.href.startsWith('http')) {
    link.target = '_blank';
    link.rel = 'noreferrer noopener';
  }
});

updateOpening();
const restoredProject = readAutosave();
if (restoredProject) {
  try {
    modeler.loadProject(restoredProject, { resetHistory: true });
    showToast('Восстановлена последняя локально сохранённая версия.');
  } catch (error) {
    console.warn('Massa restore failed', error);
  }
}
persistenceReady = true;
saveProjectLocally(modeler.serializeProject());
setView(location.hash.slice(1) || 'studio', false);

window.Massa = Object.freeze({
  version: '1.3.0',
  getProject: () => modeler.serializeProject(),
  loadProject: (project) => modeler.loadProject(project, { resetHistory: true }),
  newProject: () => modeler.newProject(),
  addMass: (values = {}) => modeler.addMass(values),
  duplicateSelected: () => modeler.duplicateSelected(),
  updateSelected: (values) => modeler.updateSelected(values),
  setTool: (tool) => modeler.setTool(tool),
  setOpeningSize: (values) => modeler.setOpeningSize(values),
  pointerDown: (clientX, clientY) => modeler.inputDown(clientX, clientY, 'api'),
  pointerMove: (clientX, clientY) => modeler.inputMove(clientX, clientY, 'api'),
  pointerUp: () => modeler.inputUp(),
  exportGLB: () => modeler.exportGLB(),
  exportOBJ: () => modeler.exportOBJ(),
  capturePNG: () => modeler.capturePNG(),
});

// Compatibility alias for integrations built against the first AirForm prototype.
window.AirForm = window.Massa;

window.addEventListener('beforeunload', () => {
  window.clearTimeout(saveTimer);
  saveProjectLocally(modeler.serializeProject());
  handInput.stop();
  modeler.dispose();
});
